import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Form } from '@fuxi/eevee-ui';
import { FormItemProps, Rule } from 'antd/es/form';
import { InputProps } from 'antd/es/input';

import classnames from 'classnames/bind';
import { isValidPhoneNumber, isPossiblePhoneNumber } from 'libphonenumber-js/mobile';
import { NamePath } from 'antd/es/form/interface';
import { ConfigProvider, CountryPhoneInput, countryJsonEn, countryJsonZh } from '@yaotai/components';
import { EN, ZH } from '@yaotai/common-utils';
import { LabelName } from '../../components';
import { POSSIBLE_COUNTRY_SHORT_LIST } from './helper';
import styles from './style.module.less';

export * from './CountryPhone';

const cx = classnames.bind(styles);

export const COUNTRY_PHONE_LOCAL_CONFIG: Record<string, Record<string, string>> = {
  [EN]: {
    mobileNumber: 'Mobile Number',
    mobilePlaceholder: 'Phone number',
    errorPhoneText: 'Phone number format error',
  },
  [ZH]: {
    mobileNumber: '手机',
    mobilePlaceholder: '请输入手机号',
    errorPhoneText: '手机格式不正确',
  },
};

type Props = {
  hasLabel?: boolean;
  hidden?: boolean;
  info?: string;
  inputSize?: string;
  isMobile?: boolean;
  labelName?: string;
  labelStyle?: React.CSSProperties;
  language?: string;
  moreInputProps?: Omit<InputProps, 'defaultValue' | 'value' | 'onChange'>;
  moreValidateRule?: Rule[];
  name?: NamePath;
  placeholder?: string;
  required?: boolean;
  type?: 'admin' | 'center' | 'reception';
  validateFirst?: boolean;
  validateTrigger?: string | string[];
} & FormItemProps;

const CountryPhoneContainer = styled.div<{ isMobile?: boolean }>`
  ${props =>
    props.isMobile &&
    `
    .ant-select-arrow {
      margin-top: 0;
    }
  `}
`;
const MobileInputStyle = createGlobalStyle`
  .ant-input {
    font-size: var(--font-size-xl);
  }
  .ant-select-selection-item{
    font-size: var(--font-size-xl);
  }
 
  .ant-select-item-option-content{
    font-size: var(--font-size-xl);
  }
  .ant-select-arrow{
    font-size: var(--font-size-lg);
  }
  .ant-form-item-explain{
    font-size: var(--font-size-xl);
  }
`;

export const CountryPhoneField = ({
  hasLabel = true,
  required = true,
  validateTrigger = ['onBlur'],
  moreValidateRule = [],
  info,
  hidden = false,
  labelStyle,
  language = ZH,
  placeholder,
  isMobile = false,
  moreInputProps,
  inputSize = 'large',
  type = 'center',
  name = 'phone',
  label = '手机',
  validateFirst = true,
  className,
}: Props) => {
  return (
    <CountryPhoneContainer isMobile={isMobile}>
      {isMobile && <MobileInputStyle></MobileInputStyle>}
      <ConfigProvider
        locale={language === EN ? countryJsonEn : countryJsonZh}
        areaSorter={() => 0}
        areaMapper={area => {
          return {
            ...area,
            emoji: null,
          };
        }}>
        <Form.Item
          required={required}
          label={
            hasLabel ? (
              type !== 'admin' ? (
                <LabelName
                  name={COUNTRY_PHONE_LOCAL_CONFIG[language]['mobileNumber']}
                  info={info}
                  labelStyle={labelStyle}
                />
              ) : (
                label
              )
            ) : undefined
          }
          className={cx('international-phone-field', inputSize, type, className)}
          name={name}
          hidden={hidden}
          validateTrigger={validateTrigger}
          initialValue={{
            code: 86,
            phone: '',
            short: 'CN',
          }}
          validateFirst={validateFirst}
          rules={[
            {
              validator: async (_, value) => {
                if (!value) return Promise.resolve();
                const { phone, short } = value;
                if (!phone && !required) return Promise.resolve();
                else if (!phone) return Promise.reject(COUNTRY_PHONE_LOCAL_CONFIG[language]['mobilePlaceholder']);
                const _isValidFunc = POSSIBLE_COUNTRY_SHORT_LIST.includes(short)
                  ? isPossiblePhoneNumber
                  : isValidPhoneNumber;
                return /^[\d]+$/.test(phone) && _isValidFunc(phone, short)
                  ? Promise.resolve()
                  : Promise.reject(COUNTRY_PHONE_LOCAL_CONFIG[language]['errorPhoneText']);
              },
            },
            ...moreValidateRule,
          ]}>
          <CountryPhoneInput
            selectProps={{ showSearch: !isMobile }}
            placeholder={placeholder}
            {...moreInputProps}
            className={cx('mobile-input')}
          />
        </Form.Item>
      </ConfigProvider>
    </CountryPhoneContainer>
  );
};

export * from './CountryPhoneFieldFormItem';
export * from './CountryPhoneFieldFormItemMobile';
